// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: shared controls for Konvo pages. Use these instead of per-page pill and
// icon-button classes so WhatsApp, Tasks, Mail and Home look like one app and
// every theme restyles them together. See KonvoControls.module.css for the look.

import {CaretRightIcon, CheckIcon} from '@app/features/ui/icons/Icons';
import styles from '@app/features/ui/konvo_controls/KonvoControls.module.css';
import {IconCheck, IconExclamationMark, IconMinus} from '@tabler/icons-react';
import {clsx} from 'clsx';
import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';

type NativeButton = React.ButtonHTMLAttributes<HTMLButtonElement>;

/** Row of controls. `scroll` makes it a single horizontally scrolling line (filter rows on phones). */
export function Toolbar({
	scroll = false,
	className,
	...rest
}: React.HTMLAttributes<HTMLDivElement> & {scroll?: boolean}) {
	return <div className={clsx(styles.toolbar, scroll && styles.toolbarScroll, className)} {...rest} />;
}

export function ToolbarSpacer() {
	return <span className={styles.spacer} aria-hidden="true" />;
}

/** Filter or toggle chip. Pass `pressed` for toggles; pass `selected` for one-of-many filters. */
export const Chip = React.forwardRef<
	HTMLButtonElement,
	NativeButton & {pressed?: boolean; selected?: boolean; count?: number | null; icon?: React.ReactNode}
>(({pressed, selected, count, icon, className, children, type = 'button', ...rest}, ref) => (
	<button
		ref={ref}
		type={type}
		className={clsx(styles.chip, icon && !children && !(count != null && count > 0) && styles.chipIcon, className)}
		aria-pressed={pressed}
		// aria-selected is only valid on tab/option roles; callers pass role="tab" with `selected`.
		{...(selected != null ? {'aria-selected': selected} : {})}
		{...rest}
	>
		{(pressed || selected) && children != null && !icon ? (
			<CheckIcon className={styles.chipCheck} weight="bold" />
		) : (
			icon
		)}
		{children}
		{count != null && count > 0 && <span className={styles.chipCount}>{count > 99 ? '99+' : count}</span>}
	</button>
));
Chip.displayName = 'Chip';

export interface SegmentedOption<T extends string> {
	value: T;
	/** Visible text. Omit for an icon-only segment; `label` is then the accessible name. */
	children?: React.ReactNode;
	label?: string;
	icon?: React.ReactNode;
	count?: number | null;
	disabled?: boolean;
	'data-flx'?: string;
}

/**
 * True for a moment after `value` changes: a segmented control's lens squashes
 * while it travels to the new option, then settles (see .segmentedThumbMoving).
 * Shared by every one-of-N selector that draws the glass lens.
 */
export function useSegmentedSquash(value: unknown): boolean {
	const [moving, setMoving] = useState(false);
	const previous = useRef(value);
	const settle = useRef<number | null>(null);
	useLayoutEffect(() => {
		if (Object.is(previous.current, value)) return;
		previous.current = value;
		setMoving(true);
		if (settle.current != null) window.clearTimeout(settle.current);
		settle.current = window.setTimeout(() => setMoving(false), 180);
	}, [value]);
	useEffect(
		() => () => {
			if (settle.current != null) window.clearTimeout(settle.current);
		},
		[],
	);
	return moving;
}

/**
 * One-of-N toggle: the "liquid" glass selector. A soft track, and a floating
 * glass lens that slides to the chosen option with a small squash. Use it for
 * grouped choices (Open/All, List/Board, mail folders, calendar views) where
 * the options belong together; use Chips for independent filters.
 */
export function Segmented<T extends string>({
	value,
	options,
	onChange,
	onReselect,
	grow = false,
	compact = false,
	role = 'group',
	className,
	...rest
}: Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> & {
	value: T;
	options: ReadonlyArray<SegmentedOption<T>>;
	onChange: (value: T) => void;
	/** Called when the option that is already chosen is pressed again. */
	onReselect?: (value: T) => void;
	/** Stretch to fill the toolbar row. */
	grow?: boolean;
	/** 38px instead of 44px, for page toolbars and section headings. */
	compact?: boolean;
	role?: 'group' | 'tablist';
}) {
	const trackRef = useRef<HTMLDivElement>(null);
	const [thumb, setThumb] = useState<{left: number; width: number} | null>(null);
	const moving = useSegmentedSquash(value);
	// Measure the pressed option so the lens can slide to it; re-measure on
	// resize and when the options or labels change.
	useLayoutEffect(() => {
		const track = trackRef.current;
		if (!track) return;
		const measure = () => {
			const on = track.querySelector<HTMLElement>('[data-segment-on="true"]');
			if (!on) {
				setThumb(null);
				return;
			}
			setThumb({left: on.offsetLeft, width: on.offsetWidth});
		};
		measure();
		// Web fonts loading late widen the options without resizing the track.
		void document.fonts?.ready.then(measure);
		if (typeof ResizeObserver === 'undefined') return;
		const observer = new ResizeObserver(measure);
		observer.observe(track);
		return () => observer.disconnect();
	}, [value, options]);
	// A track that scrolls sideways (phones) keeps the chosen option in view, also once web
	// fonts have loaded and widened the options before it.
	useLayoutEffect(() => {
		const track = trackRef.current;
		if (!track) return;
		const reveal = () => {
			const on = track.querySelector<HTMLElement>('[data-segment-on="true"]');
			if (!on || track.scrollWidth <= track.clientWidth) return;
			const left = on.offsetLeft;
			const right = left + on.offsetWidth;
			if (left >= track.scrollLeft && right <= track.scrollLeft + track.clientWidth) return;
			track.scrollLeft = left - (track.clientWidth - on.offsetWidth) / 2;
		};
		reveal();
		void document.fonts?.ready.then(reveal);
	}, [value]);
	const tabs = role === 'tablist';
	return (
		<div
			ref={trackRef}
			role={role}
			className={clsx(styles.segmented, grow && styles.segmentedGrow, compact && styles.segmentedCompact, className)}
			{...rest}
		>
			<span
				className={clsx(
					styles.segmentedThumb,
					thumb == null && styles.segmentedThumbStill,
					moving && styles.segmentedThumbMoving,
				)}
				style={thumb ? {width: thumb.width + 2, translate: `${thumb.left - 1}px 0`} : {opacity: 0}}
				aria-hidden="true"
			/>
			{options.map((option) => {
				const on = option.value === value;
				const iconOnly = option.icon != null && option.children == null;
				return (
					<button
						key={option.value}
						type="button"
						role={tabs ? 'tab' : undefined}
						className={clsx(styles.segment, iconOnly && styles.segmentIcon)}
						{...(tabs ? {'aria-selected': on} : {'aria-pressed': on})}
						aria-label={option.label}
						title={iconOnly ? option.label : undefined}
						disabled={option.disabled}
						data-segment-on={on ? 'true' : undefined}
						data-flx={option['data-flx']}
						onClick={() => {
							if (!on) onChange(option.value);
							else onReselect?.(option.value);
						}}
					>
						{option.icon}
						{option.children}
						{option.count != null && option.count > 0 && (
							<span className={styles.chipCount}>{option.count > 99 ? '99+' : option.count}</span>
						)}
					</button>
				);
			})}
		</div>
	);
}

/** Round icon-only control. Always give it a label; it becomes the aria-label and tooltip. */
export const IconButton = React.forwardRef<
	HTMLButtonElement,
	Omit<NativeButton, 'aria-label' | 'title' | 'children'> & {
		label: string;
		icon: React.ReactNode;
		active?: boolean;
		filled?: boolean;
		/** The one thing to do on the surface (New task, Compose): dark, with a shadow. */
		primary?: boolean;
		round?: boolean;
		badge?: number | boolean | null;
	}
>(({label, icon, active, filled, primary, round, badge, className, type = 'button', ...rest}, ref) => (
	<button
		ref={ref}
		type={type}
		className={clsx(
			styles.iconButton,
			filled && styles.iconButtonFilled,
			primary && styles.iconButtonPrimary,
			round && styles.iconButtonRound,
			active && styles.iconButtonActive,
			className,
		)}
		aria-label={label}
		title={label}
		{...rest}
	>
		{icon}
		{badge === true && <span className={clsx(styles.iconBadge, styles.iconBadgeDot)} aria-hidden="true" />}
		{typeof badge === 'number' && badge > 0 && (
			<span className={styles.iconBadge} aria-hidden="true">
				{badge > 99 ? '99+' : badge}
			</span>
		)}
	</button>
));
IconButton.displayName = 'IconButton';

export type ActionTone = 'primary' | 'secondary' | 'quiet' | 'danger';

/** Labelled button. `primary` is the one thing to do on a surface; use it once per surface. */
export const ActionButton = React.forwardRef<
	HTMLButtonElement,
	NativeButton & {tone?: ActionTone; small?: boolean; block?: boolean; icon?: React.ReactNode}
>(({tone = 'secondary', small, block, icon, className, children, type = 'button', ...rest}, ref) => (
	<button
		ref={ref}
		type={type}
		className={clsx(
			styles.action,
			tone === 'primary' && styles.actionPrimary,
			tone === 'secondary' && styles.actionSecondary,
			tone === 'quiet' && styles.actionQuiet,
			tone === 'danger' && styles.actionDanger,
			small && styles.actionSmall,
			block && styles.actionBlock,
			className,
		)}
		{...rest}
	>
		{icon}
		{children}
	</button>
));
ActionButton.displayName = 'ActionButton';

/**
 * Inline status strip. Only render it when there is something to say. With a `title` it becomes a
 * small card: the title on top, the text under it, then `details` (facts in a box, e.g. a code and
 * a reference) and `actions` (a row of buttons) below.
 */
export function Notice({
	tone = 'neutral',
	icon,
	title,
	children,
	action,
	details,
	actions,
	className,
	...rest
}: Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> & {
	tone?: 'neutral' | 'warning' | 'danger';
	icon?: React.ReactNode;
	/** A heading: the notice lays out as a card. */
	title?: React.ReactNode;
	action?: React.ReactNode;
	/** Facts under the text, in a hairline box. */
	details?: React.ReactNode;
	/** Buttons under everything. */
	actions?: React.ReactNode;
}) {
	const card = title != null || details != null || actions != null;
	return (
		<div
			role={tone === 'danger' ? 'alert' : 'status'}
			className={clsx(
				styles.notice,
				tone === 'warning' && styles.noticeWarning,
				tone === 'danger' && styles.noticeDanger,
				card && styles.noticeCard,
				className,
			)}
			{...rest}
		>
			{icon && <span className={styles.noticeIcon}>{icon}</span>}
			{card ? (
				<span className={styles.noticeBody}>
					{title != null && <span className={styles.noticeTitle}>{title}</span>}
					{children != null && <span className={styles.noticeText}>{children}</span>}
					{details != null && <span className={styles.noticeDetails}>{details}</span>}
					{actions != null && <span className={styles.noticeActions}>{actions}</span>}
				</span>
			) : (
				<span className={styles.noticeText}>{children}</span>
			)}
			{action}
		</div>
	);
}

export type StatusState = 'ok' | 'warn' | 'bad' | 'off';

/**
 * A small flat status mark: an 18px rounded square in the theme's status colour with a white
 * glyph (check, !, !, dash). Pass `label` when nothing next to it says the state in words.
 */
export function StatusBadge({
	state,
	label,
	size = 18,
	className,
	...rest
}: Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> & {state: StatusState; label?: string; size?: number}) {
	const glyph = Math.round(size * 0.72);
	return (
		<span
			className={clsx(
				styles.statusBadge,
				state === 'ok' && styles.statusOk,
				state === 'warn' && styles.statusWarn,
				state === 'bad' && styles.statusBad,
				state === 'off' && styles.statusOff,
				className,
			)}
			style={size === 18 ? undefined : {width: size, height: size}}
			data-state={state}
			{...(label ? {role: 'img', 'aria-label': label, title: label} : {'aria-hidden': true})}
			{...rest}
		>
			{state === 'ok' ? (
				<IconCheck size={glyph} stroke={3} />
			) : state === 'off' ? (
				<IconMinus size={glyph} stroke={3} />
			) : (
				<IconExclamationMark size={glyph} stroke={3} />
			)}
		</span>
	);
}

/** Grouped rows for detail surfaces (contact info, group info, settings-like lists). */
export function InfoGroup({
	label,
	className,
	children,
	...rest
}: React.HTMLAttributes<HTMLDivElement> & {label?: string}) {
	return (
		<div className={clsx(styles.infoGroup, className)} {...rest}>
			{label && <span className={styles.infoGroupLabel}>{label}</span>}
			{children}
		</div>
	);
}

/** One row in an InfoGroup. Give it `onClick` to make it a button with a chevron. */
export function InfoRow({
	icon,
	title,
	hint,
	value,
	valueOn,
	trail,
	danger,
	chevron,
	onClick,
	disabled,
	className,
	...rest
}: Omit<NativeButton, 'title' | 'value'> & {
	icon?: React.ReactNode;
	title: React.ReactNode;
	hint?: React.ReactNode;
	/** Short state pill on the right, e.g. "On" / "Off". */
	value?: React.ReactNode;
	valueOn?: boolean;
	trail?: React.ReactNode;
	danger?: boolean;
	chevron?: boolean;
}) {
	const interactive = Boolean(onClick);
	const content = (
		<>
			{icon && <span className={styles.infoRowIcon}>{icon}</span>}
			<span className={styles.infoRowBody}>
				<span>{title}</span>
				{hint && <span className={styles.infoRowHint}>{hint}</span>}
			</span>
			{(value != null || trail || (chevron ?? interactive)) && (
				<span className={styles.infoRowTrail}>
					{trail}
					{value != null && (
						<span className={clsx(styles.infoRowValue, valueOn && styles.infoRowValueOn)}>{value}</span>
					)}
					{(chevron ?? interactive) && <CaretRightIcon size={16} />}
				</span>
			)}
		</>
	);
	const cls = clsx(styles.infoRow, interactive && styles.infoRowButton, danger && styles.infoRowDanger, className);
	if (interactive) {
		return (
			<button type="button" className={cls} onClick={onClick} disabled={disabled} {...rest}>
				{content}
			</button>
		);
	}
	return <div className={cls}>{content}</div>;
}

/** Avatar, name and a line of meta at the top of a contact or group surface. */
export function Identity({
	avatar,
	name,
	meta,
	actions,
}: {
	avatar: React.ReactNode;
	name: React.ReactNode;
	meta?: React.ReactNode;
	actions?: React.ReactNode;
}) {
	return (
		<div className={styles.identity}>
			{avatar}
			<span className={styles.identityName}>{name}</span>
			{meta && <span className={styles.identityMeta}>{meta}</span>}
			{actions && <div className={styles.identityActions}>{actions}</div>}
		</div>
	);
}

export function IdentityAction({
	icon,
	label,
	...rest
}: Omit<NativeButton, 'children'> & {icon: React.ReactNode; label: string}) {
	return (
		<button type="button" className={styles.identityAction} {...rest}>
			{icon}
			{label}
		</button>
	);
}

export const identityAvatarClass = styles.identityAvatar;
